import { useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useBillingStore } from '../store/billingStore';
import type {
  TimeEntry,
  Expense,
  Bill,
  LineItem,
  InvoiceTheme,
  RevisionReason,
  Payment,
  PaymentStatistics,
  RecordPaymentDto,
  ReversalReason,
  TimeEntryFilters,
  ExpenseFilters,
  BillFilters,
  BillingStatus,
  CreateTimeEntryDto,
  UpdateTimeEntryDto,
  CreateExpenseDto,
  UpdateExpenseDto,
  CreateBillDto,
  UpdateBillDto,
  CreateManualBillDto,
  CreateLineItemDto,
  UpdateLineItemDto,
  CreateInvoiceThemeDto,
  UpdateInvoiceThemeDto,
} from '../types/billing.types';

const API_BASE = '';

export const useBilling = () => {
  const { keycloak } = useKeycloak();
  const store = useBillingStore();

  const getAuthHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${keycloak?.token || ''}`,
    }),
    [keycloak?.token]
  );

  // ── Time Entries ──

  const fetchTimeEntries = useCallback(
    async (filters?: TimeEntryFilters) => {
      try {
        store.incrementLoading();
        store.setError(null);
        const freshFilters = useBillingStore.getState().timeEntryFilters;
        const merged = { ...freshFilters, ...filters };
        const params = new URLSearchParams();
        Object.entries(merged).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
        });
        const res = await fetch(
          `${API_BASE}/api/v1/billing/time-entries?${params}`,
          { headers: getAuthHeaders() }
        );
        if (!res.ok) throw new Error(`Failed to fetch time entries: ${res.statusText}`);
        const json = await res.json();
        store.setTimeEntries(json.data || [], json.pagination || { total: 0, offset: 0, has_more: false });
        return json;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch time entries';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders]
  );

  const fetchTimeEntryStats = useCallback(
    async (filters?: { user_id?: string; date_from?: string; date_to?: string }) => {
      try {
        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([k, v]) => {
            if (v) params.append(k, v);
          });
        }
        const res = await fetch(
          `${API_BASE}/api/v1/billing/time-entries/statistics?${params}`,
          { headers: getAuthHeaders() }
        );
        if (!res.ok) throw new Error(`Failed to fetch time entry stats: ${res.statusText}`);
        const json = await res.json();
        store.setTimeEntryStats(json.data);
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch stats';
        store.setError(msg);
        throw err;
      }
    },
    [getAuthHeaders]
  );

  const createTimeEntry = useCallback(
    async (dto: CreateTimeEntryDto) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/time-entries`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(dto),
        });
        if (!res.ok) throw new Error(`Failed to create time entry: ${res.statusText}`);
        const json = await res.json();
        await fetchTimeEntries();
        await fetchTimeEntryStats();
        return json.data as TimeEntry;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to create time entry';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchTimeEntries, fetchTimeEntryStats]
  );

  const updateTimeEntry = useCallback(
    async (id: string, dto: UpdateTimeEntryDto) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/time-entries/${id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(dto),
        });
        if (!res.ok) throw new Error(`Failed to update time entry: ${res.statusText}`);
        const json = await res.json();
        await fetchTimeEntries();
        await fetchTimeEntryStats();
        return json.data as TimeEntry;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to update time entry';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchTimeEntries, fetchTimeEntryStats]
  );

  const deleteTimeEntry = useCallback(
    async (id: string) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/time-entries/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error(`Failed to delete time entry: ${res.statusText}`);
        await fetchTimeEntries();
        await fetchTimeEntryStats();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to delete time entry';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchTimeEntries, fetchTimeEntryStats]
  );

  const startTimer = useCallback(
    async (id: string) => {
      const res = await fetch(`${API_BASE}/api/v1/billing/time-entries/${id}/start-timer`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to start timer: ${res.statusText}`);
      const json = await res.json();
      await fetchTimeEntries();
      return json.data as TimeEntry;
    },
    [getAuthHeaders, fetchTimeEntries]
  );

  const pauseTimer = useCallback(
    async (id: string) => {
      const res = await fetch(`${API_BASE}/api/v1/billing/time-entries/${id}/pause-timer`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to pause timer: ${res.statusText}`);
      const json = await res.json();
      await fetchTimeEntries();
      return json.data as TimeEntry;
    },
    [getAuthHeaders, fetchTimeEntries]
  );

  const stopTimer = useCallback(
    async (id: string) => {
      const res = await fetch(`${API_BASE}/api/v1/billing/time-entries/${id}/stop-timer`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to stop timer: ${res.statusText}`);
      const json = await res.json();
      await fetchTimeEntries();
      await fetchTimeEntryStats();
      return json.data as TimeEntry;
    },
    [getAuthHeaders, fetchTimeEntries, fetchTimeEntryStats]
  );

  const bulkApproveTimeEntries = useCallback(
    async (ids: string[]) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/time-entries/bulk-approval`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ time_entry_ids: ids }),
        });
        if (!res.ok) throw new Error(`Failed to approve time entries: ${res.statusText}`);
        const json = await res.json();
        store.clearSelections();
        await fetchTimeEntries();
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to approve time entries';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchTimeEntries]
  );

  const transitionTimeEntryBillingStatus = useCallback(
    async (timeEntryId: string, status: BillingStatus, reason?: string) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/time-entries/${timeEntryId}/billing-status`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({ status, reason }),
        });
        if (!res.ok) throw new Error(`Failed to update billing status: ${res.statusText}`);
        const json = await res.json();
        await fetchTimeEntries();
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to update billing status';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchTimeEntries]
  );

  const bulkTransitionTimeEntryBillingStatus = useCallback(
    async (ids: string[], status: BillingStatus, reason?: string) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/time-entries/bulk-billing-status`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ time_entry_ids: ids, status, reason }),
        });
        if (!res.ok) throw new Error(`Failed to bulk update billing status: ${res.statusText}`);
        const json = await res.json();
        store.clearSelections();
        await fetchTimeEntries();
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to bulk update billing status';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchTimeEntries]
  );

  // ── Expenses ──

  const fetchExpenses = useCallback(
    async (filters?: ExpenseFilters) => {
      try {
        store.incrementLoading();
        store.setError(null);
        const freshFilters = useBillingStore.getState().expenseFilters;
        const merged = { ...freshFilters, ...filters };
        const params = new URLSearchParams();
        Object.entries(merged).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
        });
        const res = await fetch(
          `${API_BASE}/api/v1/billing/expenses?${params}`,
          { headers: getAuthHeaders() }
        );
        if (!res.ok) throw new Error(`Failed to fetch expenses: ${res.statusText}`);
        const json = await res.json();
        store.setExpenses(json.data || [], json.pagination || { total: 0, offset: 0, has_more: false });
        return json;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch expenses';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders]
  );

  const fetchExpenseStats = useCallback(
    async (filters?: { user_id?: string; date_from?: string; date_to?: string }) => {
      try {
        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([k, v]) => {
            if (v) params.append(k, v);
          });
        }
        const res = await fetch(
          `${API_BASE}/api/v1/billing/expenses/statistics?${params}`,
          { headers: getAuthHeaders() }
        );
        if (!res.ok) throw new Error(`Failed to fetch expense stats: ${res.statusText}`);
        const json = await res.json();
        store.setExpenseStats(json.data);
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch expense stats';
        store.setError(msg);
        throw err;
      }
    },
    [getAuthHeaders]
  );

  const createExpense = useCallback(
    async (dto: CreateExpenseDto) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/expenses`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(dto),
        });
        if (!res.ok) throw new Error(`Failed to create expense: ${res.statusText}`);
        const json = await res.json();
        await fetchExpenses();
        await fetchExpenseStats();
        return json.data as Expense;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to create expense';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchExpenses, fetchExpenseStats]
  );

  const updateExpense = useCallback(
    async (id: string, dto: UpdateExpenseDto) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/expenses/${id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(dto),
        });
        if (!res.ok) throw new Error(`Failed to update expense: ${res.statusText}`);
        const json = await res.json();
        await fetchExpenses();
        await fetchExpenseStats();
        return json.data as Expense;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to update expense';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchExpenses, fetchExpenseStats]
  );

  const deleteExpense = useCallback(
    async (id: string) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/expenses/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error(`Failed to delete expense: ${res.statusText}`);
        await fetchExpenses();
        await fetchExpenseStats();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to delete expense';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchExpenses, fetchExpenseStats]
  );

  const bulkApproveExpenses = useCallback(
    async (ids: string[]) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/expenses/bulk-approval`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ expense_ids: ids }),
        });
        if (!res.ok) throw new Error(`Failed to approve expenses: ${res.statusText}`);
        const json = await res.json();
        store.clearSelections();
        await fetchExpenses();
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to approve expenses';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchExpenses]
  );

  const transitionExpenseBillingStatus = useCallback(
    async (expenseId: string, status: BillingStatus, reason?: string) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/expenses/${expenseId}/billing-status`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({ status, reason }),
        });
        if (!res.ok) throw new Error(`Failed to update billing status: ${res.statusText}`);
        const json = await res.json();
        await fetchExpenses();
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to update billing status';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchExpenses]
  );

  const bulkTransitionExpenseBillingStatus = useCallback(
    async (ids: string[], status: BillingStatus, reason?: string) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/expenses/bulk-billing-status`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ expense_ids: ids, status, reason }),
        });
        if (!res.ok) throw new Error(`Failed to bulk update billing status: ${res.statusText}`);
        const json = await res.json();
        store.clearSelections();
        await fetchExpenses();
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to bulk update billing status';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchExpenses]
  );

  // ── Bills / Invoices ──

  const fetchBills = useCallback(
    async (filters?: BillFilters) => {
      try {
        store.incrementLoading();
        store.setError(null);
        const freshFilters = useBillingStore.getState().billFilters;
        const merged = { ...freshFilters, ...filters };
        const params = new URLSearchParams();
        Object.entries(merged).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
        });
        const res = await fetch(
          `${API_BASE}/api/v1/billing/bills?${params}`,
          { headers: getAuthHeaders() }
        );
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || `Failed to fetch bills: ${res.statusText}`);
        }
        const json = await res.json();
        store.setBills(json.data || [], json.pagination || { total: 0, offset: 0, has_more: false });
        return json;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch bills';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders]
  );

  const fetchBillStats = useCallback(
    async (filters?: { matter_id?: string; client_id?: string; date_from?: string; date_to?: string }) => {
      try {
        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([k, v]) => {
            if (v) params.append(k, v);
          });
        }
        const res = await fetch(
          `${API_BASE}/api/v1/billing/bills/statistics?${params}`,
          { headers: getAuthHeaders() }
        );
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || `Failed to fetch bill stats: ${res.statusText}`);
        }
        const json = await res.json();
        store.setBillStats(json.data);
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch bill stats';
        store.setError(msg);
        throw err;
      }
    },
    [getAuthHeaders]
  );

  const createBill = useCallback(
    async (dto: CreateBillDto) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/bills`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(dto),
        });
        if (!res.ok) {
          const errorJson = await res.json().catch(() => null);
          throw new Error(errorJson?.error || errorJson?.description || `Failed to create bill: ${res.statusText}`);
        }
        const json = await res.json();
        await fetchBills();
        await fetchBillStats();
        return json.data as Bill;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to create bill';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchBills, fetchBillStats]
  );

  const updateBill = useCallback(
    async (id: string, dto: UpdateBillDto) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/bills/${id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(dto),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || `Failed to update bill: ${res.statusText}`);
        }
        const json = await res.json();
        await fetchBills();
        return json.data as Bill;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to update bill';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchBills]
  );

  const transitionBillStatus = useCallback(
    async (
      id: string,
      status: string,
      rejectionReason?: string,
      revisionData?: { revision_reason_id?: string; revision_reason_notes?: string }
    ) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/bills/${id}/transition`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            status,
            rejection_reason: rejectionReason,
            ...revisionData,
          }),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || `Failed to transition bill status: ${res.statusText}`);
        }
        const json = await res.json();
        await fetchBills();
        await fetchBillStats();
        return json.data as Bill;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to change bill status';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchBills, fetchBillStats]
  );

  const finalizeBill = useCallback(
    async (id: string) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/bills/${id}/finalize`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || `Failed to finalize bill: ${res.statusText}`);
        }
        const json = await res.json();
        await fetchBills();
        return json.data as Bill;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to finalize bill';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchBills]
  );

  const deleteBill = useCallback(
    async (id: string) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/bills/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || `Failed to delete bill: ${res.statusText}`);
        }
        await fetchBills();
        await fetchBillStats();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to delete bill';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchBills, fetchBillStats]
  );

  // ── Manual Bill Creation ──

  const createManualBill = useCallback(
    async (dto: CreateManualBillDto) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/bills/manual`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(dto),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || `Failed to create manual bill: ${res.statusText}`);
        }
        const json = await res.json();
        await fetchBills();
        await fetchBillStats();
        return json.data as Bill;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to create manual bill';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchBills, fetchBillStats]
  );

  // ── Single Bill Detail ──

  const fetchBill = useCallback(
    async (billId: string) => {
      try {
        store.incrementLoading();
        store.setError(null);
        const res = await fetch(`${API_BASE}/api/v1/billing/bills/${billId}`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || `Failed to fetch bill: ${res.statusText}`);
        }
        const json = await res.json();
        const bill = json.data;
        store.setCurrentBill(bill);
        store.setCurrentLineItems(bill.line_items || []);
        return bill as Bill;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch bill';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders]
  );

  // ── Line Item CRUD ──

  const addLineItem = useCallback(
    async (billId: string, dto: CreateLineItemDto) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/bills/${billId}/line-items`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(dto),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || `Failed to add line item: ${res.statusText}`);
        }
        const json = await res.json();
        // Refresh the bill detail to get updated totals + items
        await fetchBill(billId);
        return json.data as LineItem;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to add line item';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchBill]
  );

  const updateLineItem = useCallback(
    async (billId: string, lineItemId: string, dto: UpdateLineItemDto) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/bills/${billId}/line-items/${lineItemId}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(dto),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || `Failed to update line item: ${res.statusText}`);
        }
        const json = await res.json();
        await fetchBill(billId);
        return json.data as LineItem;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to update line item';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchBill]
  );

  const removeLineItem = useCallback(
    async (billId: string, lineItemId: string) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/bills/${billId}/line-items/${lineItemId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || `Failed to remove line item: ${res.statusText}`);
        }
        await fetchBill(billId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to remove line item';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [getAuthHeaders, fetchBill]
  );

  const reorderLineItems = useCallback(
    async (billId: string, orderedIds: string[]) => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/billing/bills/${billId}/line-items/reorder`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ ordered_ids: orderedIds }),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || `Failed to reorder line items: ${res.statusText}`);
        }
        await fetchBill(billId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to reorder line items';
        store.setError(msg);
        throw err;
      }
    },
    [getAuthHeaders, fetchBill]
  );

  // ── Invoice Themes ──

  const fetchThemes = useCallback(
    async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/billing/invoice-themes`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error('Failed to fetch themes');
        const json = await res.json();
        store.setThemes(json.data || []);
        return json.data as InvoiceTheme[];
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch themes';
        store.setError(msg);
        throw err;
      }
    },
    [getAuthHeaders]
  );

  const fetchThemeById = useCallback(
    async (themeId: string): Promise<InvoiceTheme> => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/billing/invoice-themes/${themeId}`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error('Failed to fetch theme');
        const json = await res.json();
        return json.data as InvoiceTheme;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch theme';
        store.setError(msg);
        throw err;
      }
    },
    [getAuthHeaders]
  );

  const createTheme = useCallback(
    async (dto: CreateInvoiceThemeDto) => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/billing/invoice-themes`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(dto),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || 'Failed to create theme');
        }
        const json = await res.json();
        await fetchThemes();
        return json.data as InvoiceTheme;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to create theme';
        store.setError(msg);
        throw err;
      }
    },
    [getAuthHeaders, fetchThemes]
  );

  const updateTheme = useCallback(
    async (themeId: string, dto: UpdateInvoiceThemeDto) => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/billing/invoice-themes/${themeId}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(dto),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || 'Failed to update theme');
        }
        const json = await res.json();
        await fetchThemes();
        return json.data as InvoiceTheme;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to update theme';
        store.setError(msg);
        throw err;
      }
    },
    [getAuthHeaders, fetchThemes]
  );

  const deleteTheme = useCallback(
    async (themeId: string) => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/billing/invoice-themes/${themeId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || 'Failed to delete theme');
        }
        await fetchThemes();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to delete theme';
        store.setError(msg);
        throw err;
      }
    },
    [getAuthHeaders, fetchThemes]
  );

  const setDefaultTheme = useCallback(
    async (themeId: string) => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/billing/invoice-themes/${themeId}/set-default`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || 'Failed to set default theme');
        }
        await fetchThemes();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to set default theme';
        store.setError(msg);
        throw err;
      }
    },
    [getAuthHeaders, fetchThemes]
  );

  const duplicateTheme = useCallback(
    async (themeId: string, name: string) => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/billing/invoice-themes/${themeId}/duplicate`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ name }),
        });
        if (!res.ok) {
          const ej = await res.json().catch(() => null);
          throw new Error(ej?.error || 'Failed to duplicate theme');
        }
        const json = await res.json();
        await fetchThemes();
        return json.data as InvoiceTheme;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to duplicate theme';
        store.setError(msg);
        throw err;
      }
    },
    [getAuthHeaders, fetchThemes]
  );

  // ── Revision Reasons ──

  const fetchRevisionReasons = useCallback(
    async (): Promise<RevisionReason[]> => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/billing/revision-reasons`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error('Failed to fetch revision reasons');
        const json = await res.json();
        return (json.data || []) as RevisionReason[];
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch revision reasons';
        store.setError(msg);
        throw err;
      }
    },
    [getAuthHeaders]
  );

  // ── Invoice Preview & PDF ──

  const getInvoicePreviewUrl = useCallback(
    (billId: string, themeId?: string) => {
      const params = new URLSearchParams();
      if (themeId) params.set('theme_id', themeId);
      return `${API_BASE}/api/v1/billing/bills/${billId}/preview?${params}`;
    },
    []
  );

  const previewWithTheme = useCallback(
    async (billId: string, themeData: Record<string, unknown>): Promise<string> => {
      const res = await fetch(`${API_BASE}/api/v1/billing/bills/${billId}/preview-with-theme`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(themeData),
      });
      if (!res.ok) throw new Error('Failed to load theme preview');
      return res.text();
    },
    [getAuthHeaders]
  );

  const downloadInvoicePdf = useCallback(
    async (billId: string, themeId?: string) => {
      try {
        store.incrementLoading();
        const params = new URLSearchParams();
        if (themeId) params.set('theme_id', themeId);
        const res = await fetch(`${API_BASE}/api/v1/billing/bills/${billId}/pdf?${params}`, {
          headers: { Authorization: `Bearer ${keycloak?.token || ''}` },
        });
        if (!res.ok) {
          const errorBody = await res.json().catch(() => null);
          const detail = errorBody?.description || errorBody?.error || '';
          throw new Error(detail || `Failed to generate PDF (HTTP ${res.status})`);
        }
        const blob = await res.blob();
        const disposition = res.headers.get('Content-Disposition');
        const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1] || `invoice-${billId}.pdf`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to download PDF';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [keycloak?.token]
  );

  const exportLedesFile = useCallback(
    async (billId: string) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/bills/${billId}/ledes`, {
          headers: { Authorization: `Bearer ${keycloak?.token || ''}` },
        });
        if (!res.ok) {
          const errorBody = await res.json().catch(() => null);
          throw new Error(errorBody?.description || `Failed to export LEDES (HTTP ${res.status})`);
        }
        const text = await res.text();
        const disposition = res.headers.get('Content-Disposition');
        const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1] || `ledes-${billId}.txt`;
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to export LEDES';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [keycloak?.token]
  );

  const sendInvoice = useCallback(
    async (billId: string, data: { recipient_email: string; cc?: string; subject?: string; message?: string }) => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/bills/${billId}/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${keycloak?.token || ''}`,
          },
          body: JSON.stringify(data),
        });
        if (!res.ok) {
          const errorBody = await res.json().catch(() => null);
          throw new Error(errorBody?.description || `Failed to send invoice (HTTP ${res.status})`);
        }
        const json = await res.json();
        store.setCurrentBill(json.data);
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to send invoice';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [keycloak?.token]
  );

  // ── Payment operations ──

  const recordPayment = useCallback(
    async (dto: RecordPaymentDto): Promise<{ payment: Payment; bill: Bill }> => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/payments`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(dto),
        });
        if (!res.ok) {
          const errorBody = await res.json().catch(() => null);
          throw new Error(errorBody?.description || `Failed to record payment (HTTP ${res.status})`);
        }
        const json = await res.json();
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to record payment';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [keycloak?.token]
  );

  const getPaymentsForBill = useCallback(
    async (billId: string): Promise<{ payments: Payment[]; total: number }> => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/billing/payments?bill_id=${billId}`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error(`Failed to fetch payments (HTTP ${res.status})`);
        const json = await res.json();
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch payments';
        store.setError(msg);
        throw err;
      }
    },
    [keycloak?.token]
  );

  const reversePayment = useCallback(
    async (paymentId: string, reason: ReversalReason, notes?: string): Promise<{ payment: Payment; bill: Bill }> => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/payments/${paymentId}/reverse`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ reason, notes }),
        });
        if (!res.ok) {
          const errorBody = await res.json().catch(() => null);
          throw new Error(errorBody?.description || `Failed to reverse payment (HTTP ${res.status})`);
        }
        const json = await res.json();
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to reverse payment';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [keycloak?.token]
  );

  const refundPayment = useCallback(
    async (paymentId: string, amount: number, reason: string): Promise<{ payment: Payment; bill: Bill }> => {
      try {
        store.incrementLoading();
        const res = await fetch(`${API_BASE}/api/v1/billing/payments/${paymentId}/refund`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ amount, reason }),
        });
        if (!res.ok) {
          const errorBody = await res.json().catch(() => null);
          throw new Error(errorBody?.description || `Failed to refund payment (HTTP ${res.status})`);
        }
        const json = await res.json();
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to refund payment';
        store.setError(msg);
        throw err;
      } finally {
        store.decrementLoading();
      }
    },
    [keycloak?.token]
  );

  const getPaymentStatistics = useCallback(
    async (dateFrom?: string, dateTo?: string): Promise<PaymentStatistics> => {
      try {
        const params = new URLSearchParams();
        if (dateFrom) params.set('date_from', dateFrom);
        if (dateTo) params.set('date_to', dateTo);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const res = await fetch(`${API_BASE}/api/v1/billing/payments/statistics${qs}`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error(`Failed to fetch payment statistics (HTTP ${res.status})`);
        const json = await res.json();
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch payment statistics';
        store.setError(msg);
        throw err;
      }
    },
    [keycloak?.token]
  );

  return {
    // Time entry operations
    fetchTimeEntries,
    fetchTimeEntryStats,
    createTimeEntry,
    updateTimeEntry,
    deleteTimeEntry,
    startTimer,
    pauseTimer,
    stopTimer,
    bulkApproveTimeEntries,
    transitionTimeEntryBillingStatus,
    bulkTransitionTimeEntryBillingStatus,

    // Expense operations
    fetchExpenses,
    fetchExpenseStats,
    createExpense,
    updateExpense,
    deleteExpense,
    bulkApproveExpenses,
    transitionExpenseBillingStatus,
    bulkTransitionExpenseBillingStatus,

    // Bill / Invoice operations
    fetchBills,
    fetchBillStats,
    fetchBill,
    createBill,
    createManualBill,
    updateBill,
    transitionBillStatus,
    finalizeBill,
    deleteBill,

    // Line item operations
    addLineItem,
    updateLineItem,
    removeLineItem,
    reorderLineItems,

    // Theme operations
    fetchThemes,
    fetchThemeById,
    createTheme,
    updateTheme,
    deleteTheme,
    setDefaultTheme,
    duplicateTheme,

    // Revision reasons
    fetchRevisionReasons,

    // Invoice preview, PDF & delivery
    getInvoicePreviewUrl,
    previewWithTheme,
    downloadInvoicePdf,
    exportLedesFile,
    sendInvoice,

    // Payment operations
    recordPayment,
    getPaymentsForBill,
    reversePayment,
    refundPayment,
    getPaymentStatistics,

    // Store state (convenience re-exports)
    loading: useBillingStore((s) => s.loading),
    error: useBillingStore((s) => s.error),
  };
};
